import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { impersonationApi } from '@/lib/impersonation-api';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Says, unmissably, that you are not yourself.
 *
 * Rendered above everything and in a colour used nowhere else, because the failure this
 * prevents is an administrator forgetting which account they are looking at and drawing a
 * conclusion — or filing a bug — about the wrong one. It is deliberately not dismissible.
 *
 * The countdown is shown rather than hidden: the session cannot be renewed, so knowing there
 * are four minutes left is the difference between finishing a look and losing it midway.
 */
export function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.impersonation);
  const queryClient = useQueryClient();
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (impersonation === null) return;

    const tick = () => setRemaining(Math.max(0, impersonation.expiresAt - Date.now()));
    tick();
    const timer = window.setInterval(tick, 1000);

    return () => window.clearInterval(timer);
  }, [impersonation]);

  const stop = useMutation({
    mutationFn: () => impersonationApi.stop(),
    onSettled: () => {
      // Every cached response was fetched as the customer. Keeping any of it after switching
      // back would show one account's data under the other's name.
      queryClient.clear();
    },
  });

  if (impersonation === null) return null;

  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const expired = remaining <= 0;

  return (
    <div className="sticky top-0 z-50 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-warning/40 bg-warning/15 px-4 py-2 text-sm">
      <Eye className="size-4 shrink-0 text-warning" />
      <span>
        Viewing as <strong>{impersonation.subjectEmail}</strong>
        <span className="text-muted-foreground">. Read-only, signed in as {impersonation.actorEmail}</span>
      </span>

      <span className="ml-auto flex items-center gap-3">
        <span className={expired ? 'text-danger' : 'text-muted-foreground'}>
          {expired
            ? 'Session expired'
            : `${minutes}:${String(seconds).padStart(2, '0')} left`}
        </span>
        <Button size="sm" variant="outline" onClick={() => stop.mutate()} disabled={stop.isPending}>
          <LogOut /> Stop
        </Button>
      </span>
    </div>
  );
}
